import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image, pdf } from '@react-pdf/renderer';
import logoLimpia from '../assets/images/logo-limpia-xl.png';
import { getRecipientesList } from '../helpers/backend';

// URL base del backend para las imágenes
const getBaseUrl = () => {
  const URL = process.env.REACT_APP_API_URL;
  const url = URL.split("/api");
  return url[0];
};

// Registrar fuentes
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
});

// Estilos corporativos elegantes basados en ActaVisitaPDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 15,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 5,
    backgroundColor: '#ffffff',
    lineHeight: 1.0,
  },

  // Header elegante con logo
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    backgroundColor: '#ffffff',
    borderRadius: 4,
    padding: 4,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderLeftWidth: 3,
    borderLeftColor: '#28a745',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  logoSection: {
    position: 'relative',
    left: 10,
    alignItems: 'center',
    zIndex: 1,
  },
  logo: {
    width: 130,
    height: 20,
  },
  titleSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  titleSmall: {
    fontSize: 14,
    fontWeight: 1500,
    color: '#28a745',
    letterSpacing: 0.5,
    fontFamily: 'Helvetica-Bold',
  },
  headerInfoSection: {
    position: 'relative',
    right: 0,
    alignItems: 'flex-end',
    zIndex: 1,
  },
  headerInfoBox: {
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#dee2e6',
    padding: 6,
    flexDirection: 'row',
    gap: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    borderTopWidth: 2,
    borderTopColor: '#28a745',
  },
  headerInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  headerInfoItemHorizontal: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  headerInfoLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: '#495057',
    marginRight: 3,
  },
  headerInfoValue: {
    fontSize: 8,
    color: '#2c5aa0',
    fontWeight: 500,
  },
  subtitle: {
    fontSize: 6,
    color: '#666',
    marginTop: 0.5,
  },

  // Tabla de información superior elegante
  topInfoContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 2,
    marginBottom: 3,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  topInfoTable: {
    flexDirection: 'row',
  },
  topInfoCell: {
    width: '25%',
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#e9ecef',
    alignItems: 'center',
  },
  topInfoCellLast: {
    width: '25%',
    padding: 4,
    alignItems: 'center',
  },
  topInfoLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#495057',
    textAlign: 'center',
    marginBottom: 3,
  },
  topInfoValue: {
    fontSize: 10,
    color: '#2c5aa0',
    textAlign: 'center',
    fontWeight: 500,
  },

  // Layout principal con panel de recipientes
  mainLayout: {
    flexDirection: 'row',
    marginTop: 5,
    gap: 8,
  },
  
  // Panel de recipientes a la izquierda
  recipientPanel: {
    width: '25%',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e9ecef',
    padding: 6,
  },
  recipientPanelTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#28a745',
    textAlign: 'center',
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: '#28a745',
  },
  recipientItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: 3,
    padding: 4,
    marginBottom: 3,
    borderLeftWidth: 2,
    borderLeftColor: '#28a745',
  },
  recipientName: {
    fontSize: 8,
    color: '#495057',
    fontWeight: 500,
    marginBottom: 2,
  },
  recipientCode: {
    fontSize: 7,
    color: '#6c757d',
    fontStyle: 'italic',
  },
  // Estilos para recipientes en la tabla
  recipientCodeInTable: {
    fontSize: 7,
    fontWeight: 700,
    color: '#28a745',
    marginBottom: 0.5,
    letterSpacing: 0.2,
  },
  recipientNameInTable: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#27ae60',
    textAlign: 'center',
    letterSpacing: 0.1,
  },

  // Área de semanas (lado derecho)
  weeksArea: {
    width: '75%',
  },
  // Área de semanas ocupando todo el ancho
  weeksAreaFull: {
    width: '100%',
  },

  // Contenedor de cada semana - estilo más discreto
  weekContainer: {
    marginBottom: 0.5,
    backgroundColor: '#ffffff',
    borderRadius: 1,
    borderWidth: 0.5,
    borderColor: '#e9ecef',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  
  // Header de semana - estilo más discreto
  weekHeader: {
    backgroundColor: '#28a745',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    padding: 1,
  },
  weekHeaderText: {
    fontSize: 8,
    fontWeight: 700,
    textAlign: 'center',
    color: '#ffffff',
    letterSpacing: 0.2,
  },

  // Tabla de días de la semana - estilo modernTable
  weekTable: {
    borderRadius: 3,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#dee2e6',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  weekTableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
  },
  weekContent: {
    padding: 1,
  },
  rowLabelCell: {
    width: '15%',
    backgroundColor: '#f8f9fa',
    padding: 2,
    borderRightWidth: 1,
    borderRightColor: '#dee2e6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 10,
  },
  dayHeaderCell: {
    width: '12.14%',
    backgroundColor: '#f8f9fa',
    padding: 2,
    borderRightWidth: 1,
    borderRightColor: '#dee2e6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 10,
  },
  dayHeaderCellLast: {
    width: '12.14%',
    backgroundColor: '#f8f9fa',
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 10,
  },
  dayHeaderText: {
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'center',
    color: '#495057',
  },
  rowLabelText: {
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'center',
    color: '#495057',
  },

  // Filas de datos de la semana - estilo modernTable
  weekDataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
  },
  weekDataRowLast: {
    flexDirection: 'row',
  },
  dayDataCell: {
    width: '12.14%',
    padding: 2,
    borderRightWidth: 1,
    borderRightColor: '#dee2e6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 10,
  },
  dayDataCellLast: {
    width: '12.14%',
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 10,
  },

  // Contenido de cada día
  dayContent: {
    width: '100%',
    alignItems: 'center',
  },
  recipientUnits: {
    fontSize: 10,
    color: '#28a745',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 1,
  },
  visitDate: {
    fontSize: 7,
    color: '#495057',
    textAlign: 'center',
    marginBottom: 1,
  },
  visitTime: {
    fontSize: 7,
    color: '#6c757d',
    textAlign: 'center',
    marginBottom: 1,
  },
  visitName: {
    fontSize: 6,
    color: '#495057',
    textAlign: 'center',
    marginBottom: 1,
  },
  
  // Firma
  signatureContainer: {
    width: '100%',
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signatureImage: {
    maxWidth: '90%',
    maxHeight: '90%',
    objectFit: 'contain',
  },
  noSignature: {
    fontSize: 6,
    color: '#999',
    fontStyle: 'italic',
  },

  // Footer elegante
  footer: {
    position: 'absolute',
    bottom: 5,
    left: 20,
    right: 20,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    padding: 5,
    borderTopWidth: 1,
    borderTopColor: '#28a745',
  },
  footerText: {
    fontSize: 6,
    textAlign: 'justify',
    color: '#495057',
    lineHeight: 1.2,
    marginBottom: 2,
  },
  footerLinks: {
    marginBottom: 2,
  },
  footerLink: {
    fontSize: 5,
    color: '#2c5aa0',
    marginBottom: 1,
  },
  footerCode: {
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'right',
    color: '#28a745',
    fontFamily: 'Helvetica-Bold',
  },
});

// Componente para mostrar firma como imagen
const SignatureImage = ({ signaturePath }) => {
  let finalPath = null;

  if (typeof signaturePath === 'string' && signaturePath.trim() !== '') {
    if (!signaturePath.startsWith('http')) {
      const cleanPath = signaturePath.startsWith('/') ? signaturePath : `/${signaturePath}`;
      finalPath = `${getBaseUrl()}${cleanPath}`;
    } else {
      finalPath = signaturePath;
    }
  }

  if (!finalPath) {
    return (
      <View style={styles.signatureContainer}>
        <Text style={styles.noSignature}>Sin firma</Text>
      </View>
    );
  }

  return (
    <View style={styles.signatureContainer}>
      <Image
        src={finalPath}
        style={styles.signatureImage}
        onError={(error) => {
          console.error('SignatureImage - Error cargando imagen:', error);
        }}
      />
    </View>
  );
};

// Componente principal del PDF de aforo
const AforoPDFDocument = ({ visitas, casoData, recipientesData = [] }) => {
  // Obtener visitas desde casoData.aforos[0].visitas si existe
  const visitasData = casoData?.aforos?.[0]?.visitas || visitas || [];
  
  // Obtener configuración dinámica del aforo
  const totalFrecuencia = casoData?.aforos?.[0]?.total_frecuencia || 7;
  const totalSemana = casoData?.aforos?.[0]?.total_semana || 1;
  
  // Calcular anchos dinámicos de columnas basados en total_frecuencia
  const calcularAnchoColumna = () => {
    const anchoEtiqueta = 15; // 15% para la columna de etiquetas
    const anchoDisponible = 85; // 85% disponible para las columnas de días
    const anchoPorDia = anchoDisponible / totalFrecuencia;
    return {
      etiqueta: `${anchoEtiqueta}%`,
      dia: `${anchoPorDia}%`
    };
  };
  
  const anchosColumna = calcularAnchoColumna();
  
  // Crear estilos dinámicos basados en el ancho calculado
  const estilosDinamicos = StyleSheet.create({
    rowLabelCellDynamic: {
      width: anchosColumna.etiqueta,
      backgroundColor: '#f8f9fa',
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayHeaderCellDynamic: {
      width: anchosColumna.dia,
      backgroundColor: '#f8f9fa',
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayHeaderCellLastDynamic: {
      width: anchosColumna.dia,
      backgroundColor: '#f8f9fa',
      padding: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayDataCellDynamic: {
      width: anchosColumna.dia,
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayDataCellLastDynamic: {
      width: anchosColumna.dia,
      padding: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
  });
  
  // Función para agrupar visitas dinámicamente usando total_frecuencia y total_semana
  const agruparVisitasDinamicamente = () => {
    if (!visitasData || visitasData.length === 0) return [];
    
    // Ordenar visitas por fecha ascendente
    const visitasOrdenadas = [...visitasData]
      .filter(visita => visita.fecha_visita)
      .sort((a, b) => new Date(a.fecha_visita) - new Date(b.fecha_visita));
    
    if (visitasOrdenadas.length === 0) return [];
    
    const semanas = [];
    let visitaIndex = 0;
    
    // Crear semanas dinámicas basadas en total_semana
    for (let numeroSemana = 1; numeroSemana <= totalSemana && visitaIndex < visitasOrdenadas.length; numeroSemana++) {
      const visitasSemana = [];
      const diasSemana = [];
      
      // Tomar hasta total_frecuencia visitas para esta semana
      for (let i = 0; i < totalFrecuencia && visitaIndex < visitasOrdenadas.length; i++) {
        const visita = visitasOrdenadas[visitaIndex];
        visitasSemana.push(visita);
        
        // Obtener el día de la semana de la fecha
        const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
        const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
        const diaSemana = diasMap[fechaObj.getDay()];
        
        if (!diasSemana.includes(diaSemana)) {
          diasSemana.push(diaSemana);
        }
        
        visitaIndex++;
      }
      
      // Solo agregar la semana si tiene visitas
      if (visitasSemana.length > 0) {
        // Ordenar días por fecha ascendente en lugar de por orden de días de la semana
        const diasOrdenadosPorFecha = visitasSemana
          .map(visita => {
            const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
            const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
            return {
              dia: diasMap[fechaObj.getDay()],
              fecha: fechaObj
            };
          })
          .sort((a, b) => a.fecha - b.fecha)
          .map(item => item.dia);
        
        // Eliminar duplicados manteniendo el orden
        const diasUnicos = [...new Set(diasOrdenadosPorFecha)];
        
        semanas.push({
          numeroSemana,
          visitas: visitasSemana,
          diasConVisitas: diasUnicos
        });
      }
    }
    
    return semanas;
  };
  
  const semanasData = agruparVisitasDinamicamente();
  
  // Función para obtener visita por día en una semana específica
  const getVisitaPorDiaEnSemana = (dia, semanaData) => {
    if (!semanaData || !semanaData.visitas) return null;
    
    return semanaData.visitas.find(visita => {
      if (!visita.fecha_visita) return false;
      
      const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
      const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
      const diaSemanaVisita = diasMap[fechaObj.getDay()];
      
      return diaSemanaVisita === dia;
    }) || null;
  };
  

  
  // Crear mapeo de recipientes desde los datos de las visitas y la API
  const recipientesMap = {};
  
  // Primero, crear mapeo desde los datos de las visitas
  visitasData.forEach(visita => {
    if (visita.recipientes && Array.isArray(visita.recipientes)) {
      visita.recipientes.forEach(recipiente => {
        if (recipiente.parametrizacion_recipiente) {
          const param = recipiente.parametrizacion_recipiente;
          recipientesMap[param.id] = `${param.nombre} - ${param.descripcion}`;
        }
      });
    }
  });
  
  // Complementar con datos de la API si están disponibles
  if (Array.isArray(recipientesData)) {
    recipientesData.forEach(recipiente => {
      if (!recipientesMap[recipiente.id]) {
        recipientesMap[recipiente.id] = `${recipiente.nombre} - ${recipiente.descripcion}`;
      }
    });
  }

  // Obtener recipientes únicos de todas las visitas
  const getRecipientesUnicos = () => {
    const recipientesSet = new Set();
    visitasData.forEach(visita => {
      if (visita.recipientes && Array.isArray(visita.recipientes)) {
        visita.recipientes.forEach(recipiente => {
          if (recipiente.parametrizacion_recipiente && recipiente.parametrizacion_recipiente.id) {
            recipientesSet.add(recipiente.parametrizacion_recipiente.id);
          }
        });
      }
    });
    
    return Array.from(recipientesSet).map(id => {
      // Buscar primero en los datos de las visitas
      let recipienteData = null;
      visitasData.forEach(visita => {
        if (visita.recipientes && Array.isArray(visita.recipientes)) {
          visita.recipientes.forEach(recipiente => {
            if (recipiente.parametrizacion_recipiente && recipiente.parametrizacion_recipiente.id == id) {
              recipienteData = recipiente.parametrizacion_recipiente;
            }
          });
        }
      });
      
      // Si no se encuentra, buscar en los datos de la API
      if (!recipienteData && Array.isArray(recipientesData)) {
        recipienteData = recipientesData.find(r => r.id == id);
      }
      
      return {
        descripcion: recipienteData ? recipienteData.descripcion : (recipientesMap[id] || id),
        id: id
      };
    });
  };
  
  const recipientesUnicos = getRecipientesUnicos();
  
  // Obtener información del caso para el header
  const numeroPQR = casoData?.numero_reclamo || '';
  const cuentaSuscriptor = casoData?.aforos?.[0]?.cuenta_contrato || '';
  const fechaActual = new Date().toLocaleDateString('es-ES');
  
  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    // Agregar hora para evitar problemas de zona horaria
    const fechaObj = new Date(fecha + 'T12:00:00');
    return fechaObj.toLocaleDateString('es-ES');
  };
  
  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return '';
    return hora;
  };
  
  // Función para obtener unidades de recipiente por día
  const getUnidadesRecipiente = (visita) => {
    if (!visita || !visita.recipientes) return '';
    
    const totalUnidades = visita.recipientes.reduce((total, recipiente) => {
      return total + (recipiente.unidad || recipiente.unidades || 0);
    }, 0);
    
    return totalUnidades > 0 ? `${totalUnidades}` : '';
  };

  // Función para obtener detalles de recipientes por día
  const getDetalleRecipientes = (visita) => {
    if (!visita || !visita.recipientes) return '';
    
    return visita.recipientes.map(recipiente => 
      `${recipiente.tipo_recipiente}: ${recipiente.unidad || recipiente.unidades || 0}`
    ).join('\n');
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header con logo, título e información */}
        <View style={styles.header}>
          <View style={styles.logoSection}>
            <Image style={styles.logo} src={logoLimpia} />
          </View>
          <View style={styles.titleSection}>
            <Text style={styles.titleSmall}>AFORO DE LOS RESIDUOS</Text>
          </View>
          <View style={styles.headerInfoSection}>
            <View style={styles.headerInfoBox}>
              <View style={styles.headerInfoItemHorizontal}>
                <Text style={styles.headerInfoLabel}>No. DE PQRS:</Text>
                <Text style={styles.headerInfoValue}>{numeroPQR}</Text>
              </View>
              <View style={styles.headerInfoItemHorizontal}>
                <Text style={styles.headerInfoLabel}>CUENTA SUSCRIPTOR:</Text>
                <Text style={styles.headerInfoValue}>{cuentaSuscriptor}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Layout principal */}
        <View style={styles.mainLayout}>
          {/* Área de semanas - ahora ocupa todo el ancho */}
          <View style={styles.weeksAreaFull}>
            {semanasData.map((semanaData, semanaIndex) => (
              <View key={semanaData.numeroSemana} style={styles.weekContainer}>
                {/* Header de semana */}
                <View style={styles.weekHeader}>
                  <Text style={styles.weekHeaderText}>SEMANA No. {semanaData.numeroSemana}</Text>
                </View>

                {/* Contenido de la semana */}
                <View style={styles.weekContent}>
                  <View style={styles.weekTable}>
                    {/* Header de días - solo mostrar días con visitas */}
                    <View style={styles.weekTableHeader}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Recipientes</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayHeaderCellLastDynamic : estilosDinamicos.dayHeaderCellDynamic}>
                        <Text style={styles.dayHeaderText}>{dia}</Text>
                      </View>
                    ))}
                  </View>

                  {/* Filas de recipientes individuales */}
                  {recipientesUnicos.map((recipiente, recipienteIndex) => (
                    <View key={recipiente.id} style={styles.weekDataRow}>
                      <View style={estilosDinamicos.rowLabelCellDynamic}>
                        <Text style={styles.recipientNameInTable}>{recipiente.descripcion}</Text>
                      </View>
                      {semanaData.diasConVisitas.map((dia, diaIndex) => {
                        const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                        const unidadesRecipiente = visita?.recipientes?.find(r => r.parametrizacion_recipiente?.id == recipiente.id)?.unidad || '';
                        return (
                          <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                            <View style={styles.dayContent}>
                              <Text style={styles.recipientUnits}>
                                {unidadesRecipiente}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  ))}



                  {/* Fila de Fecha del Aforo */}
                  <View style={styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Fecha del Aforo</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          <View style={styles.dayContent}>
                            <Text style={styles.visitDate}>
                              {formatearFecha(visita?.fecha_visita)}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  {/* Fila de Hora de Aforo */}
                  <View style={styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Hora de Aforo</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          <View style={styles.dayContent}>
                            <Text style={styles.visitTime}>
                              {formatearHora(visita?.hora_visita)}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  {/* Fila de Nombre de Usuario */}
                  <View style={styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Nombre de Usuario</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          <View style={styles.dayContent}>
                            <Text style={styles.visitName}>
                              {visita ? (visita.nombre_representante || '') : ''}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  {/* Fila de Firma de Usuario */}
                  <View style={styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Firma de Usuario</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          {visita ? <SignatureImage signaturePath={visita.firma_representante} /> : <View />}
                        </View>
                      );
                    })}
                  </View>

                  {/* Fila de Nombre del Visitador */}
                  <View style={styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Nombre del Visitador</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          <View style={styles.dayContent}>
                            <Text style={styles.visitName}>
                              {visita ? (visita.nombre_visitador || casoData?.aforos?.[0]?.visitador_nombre || '') : ''}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  {/* Fila de Firma del Visitador */}
                  <View style={semanaIndex === semanasData.length - 1 ? styles.weekDataRowLast : styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.rowLabelText}>Firma del Visitador</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          {visita ? <SignatureImage signaturePath={visita.firma_visitador} /> : <View />}
                        </View>
                      );
                    })}
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Footer legal */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Autorizo de manera previa, expresa e informada a Ciudad Limpia para que directa o indirectamente, realice cualquier operación de tratamiento de mis Datos Personales, a nivel nacional e internacional, incluso con terceros ubicados en países con niveles adecuados de protección conforme a lo dispuesto en la Ley 1581 de 2012 y Decreto 1377 de 2013. Para conocer más sobre los derechos que les asisten a los titulares de los Datos Personales, las formas para ejercerlos y las finalidades y tipos de tratamiento a los que los Datos Personales serán sometidos, los invitamos a consultar la Política de Tratamiento de la Información de la Compañía, disponible en:
          </Text>
          <View style={styles.footerLinks}>
            <Text style={styles.footerLink}>Cali: https://www.ciudadlimpiacali.com.co/wp-content/uploads/documentos/politica_tratamiento_cali.pdf, Neiva: https://www.ciudadlimpianeiva.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf</Text>
            <Text style={styles.footerLink}>Huila: https://www.ciudadlimpiadelhulla.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf, Bogotá: https://www.ciudadlimpia.com.co/Politicas-Tratamiento-de-la-informacion.pdf</Text>
          </View>
          <Text style={styles.footerCode}>
            BFCM-03{"\n"}03/2022 Ed.7
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Función para generar y descargar PDF
export const generateAforoPDF = async (visitas, casoData) => {
  try {
    // Obtener datos de recipientes de la API
    let recipientesData = [];
    try {
      const recipientesResponse = await getRecipientesList();
      recipientesData = recipientesResponse?.data?.results || [];
    } catch (error) {
      console.error('Error obteniendo recipientes:', error);
      // Continuar con array vacío si falla
    }
    
    const doc = <AforoPDFDocument visitas={visitas} casoData={casoData} recipientesData={recipientesData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    
    // Crear enlace de descarga
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Aforo_${casoData?.name || 'Documento'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error generando PDF de aforo:', error);
    return { success: false, error };
  }
};

// Función para abrir PDF en nueva pestaña
export const openAforoPDF = async (visitas, casoData) => {
  try {
    // Obtener datos de recipientes de la API
     let recipientesData = [];
     try {
       const recipientesResponse = await getRecipientesList();
       recipientesData = recipientesResponse?.data?.results || [];
     } catch (error) {
       console.error('Error obteniendo recipientes:', error);
       // Continuar con array vacío si falla
     }
    
    const doc = <AforoPDFDocument visitas={visitas} casoData={casoData} recipientesData={recipientesData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    
    // Abrir en nueva pestaña
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    // Limpiar URL después de un tiempo
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
    
    return { success: true };
  } catch (error) {
    console.error('Error abriendo PDF de aforo:', error);
    return { success: false, error };
  }
};

// Componente para página de aforo sin Document wrapper (para incluir en otros PDFs)
export const AforoPage = ({ visitas, casoData, recipientesData = [] }) => {
  // Obtener visitas desde casoData.aforos[0].visitas si existe
  const visitasData = casoData?.aforos?.[0]?.visitas || visitas || [];
  
  // Obtener configuración dinámica del aforo
  const totalFrecuencia = casoData?.aforos?.[0]?.total_frecuencia || 7;
  const totalSemana = casoData?.aforos?.[0]?.total_semana || 1;
  
  // Calcular anchos dinámicos de columnas basados en total_frecuencia
  const calcularAnchoColumna = () => {
    const anchoEtiqueta = 15; // 15% para la columna de etiquetas
    const anchoDisponible = 85; // 85% disponible para las columnas de días
    const anchoPorDia = anchoDisponible / totalFrecuencia;
    return {
      etiqueta: `${anchoEtiqueta}%`,
      dia: `${anchoPorDia}%`
    };
  };
  
  const anchosColumna = calcularAnchoColumna();
  
  // Crear estilos dinámicos basados en el ancho calculado
  const estilosDinamicos = StyleSheet.create({
    rowLabelCellDynamic: {
      width: anchosColumna.etiqueta,
      backgroundColor: '#f8f9fa',
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayHeaderCellDynamic: {
      width: anchosColumna.dia,
      backgroundColor: '#f8f9fa',
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayHeaderCellLastDynamic: {
      width: anchosColumna.dia,
      backgroundColor: '#f8f9fa',
      padding: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayDataCellDynamic: {
      width: anchosColumna.dia,
      padding: 2,
      borderRightWidth: 1,
      borderRightColor: '#dee2e6',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
    dayDataCellLastDynamic: {
      width: anchosColumna.dia,
      padding: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 10,
    },
  });
  
  // Función para agrupar visitas dinámicamente usando total_frecuencia y total_semana
  const agruparVisitasDinamicamente = () => {
    if (!visitasData || visitasData.length === 0) return [];
    
    // Ordenar visitas por fecha ascendente
    const visitasOrdenadas = [...visitasData]
      .filter(visita => visita.fecha_visita)
      .sort((a, b) => new Date(a.fecha_visita) - new Date(b.fecha_visita));
    
    if (visitasOrdenadas.length === 0) return [];
    
    const semanas = [];
    let visitaIndex = 0;
    
    // Crear semanas dinámicas basadas en total_semana
    for (let numeroSemana = 1; numeroSemana <= totalSemana && visitaIndex < visitasOrdenadas.length; numeroSemana++) {
      const visitasSemana = [];
      const diasSemana = [];
      
      // Tomar hasta total_frecuencia visitas para esta semana
      for (let i = 0; i < totalFrecuencia && visitaIndex < visitasOrdenadas.length; i++) {
        const visita = visitasOrdenadas[visitaIndex];
        visitasSemana.push(visita);
        
        // Obtener el día de la semana de la fecha
        const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
        const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
        const diaSemana = diasMap[fechaObj.getDay()];
        
        if (!diasSemana.includes(diaSemana)) {
          diasSemana.push(diaSemana);
        }
        
        visitaIndex++;
      }
      
      // Solo agregar la semana si tiene visitas
      if (visitasSemana.length > 0) {
        // Ordenar días por fecha ascendente en lugar de por orden de días de la semana
        const diasOrdenadosPorFecha = visitasSemana
          .map(visita => {
            const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
            const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
            return {
              dia: diasMap[fechaObj.getDay()],
              fecha: fechaObj
            };
          })
          .sort((a, b) => a.fecha - b.fecha)
          .map(item => item.dia);
        
        // Eliminar duplicados manteniendo el orden
        const diasUnicos = [...new Set(diasOrdenadosPorFecha)];
        
        semanas.push({
          numeroSemana,
          visitas: visitasSemana,
          diasConVisitas: diasUnicos
        });
      }
    }
    
    return semanas;
  };
  
  const semanasData = agruparVisitasDinamicamente();
  
  // Función para obtener visita por día en una semana específica
  const getVisitaPorDiaEnSemana = (dia, semanaData) => {
    if (!semanaData || !semanaData.visitas) return null;
    
    return semanaData.visitas.find(visita => {
      if (!visita.fecha_visita) return false;
      
      const fechaObj = new Date(visita.fecha_visita + 'T12:00:00');
      const diasMap = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
      const diaSemanaVisita = diasMap[fechaObj.getDay()];
      
      return diaSemanaVisita === dia;
    }) || null;
  };
  
  // Crear mapeo de recipientes desde los datos de las visitas y la API
  const recipientesMap = {};
  
  // Primero, crear mapeo desde los datos de las visitas
  visitasData.forEach(visita => {
    if (visita.recipientes && Array.isArray(visita.recipientes)) {
      visita.recipientes.forEach(recipiente => {
        if (recipiente.parametrizacion_recipiente) {
          const param = recipiente.parametrizacion_recipiente;
          recipientesMap[param.id] = `${param.nombre} - ${param.descripcion}`;
        }
      });
    }
  });
  
  // Complementar con datos de la API si están disponibles
  if (Array.isArray(recipientesData)) {
    recipientesData.forEach(recipiente => {
      if (!recipientesMap[recipiente.id]) {
        recipientesMap[recipiente.id] = `${recipiente.nombre} - ${recipiente.descripcion}`;
      }
    });
  }

  // Obtener recipientes únicos de todas las visitas
  const getRecipientesUnicos = () => {
    const recipientesSet = new Set();
    visitasData.forEach(visita => {
      if (visita.recipientes && Array.isArray(visita.recipientes)) {
        visita.recipientes.forEach(recipiente => {
          if (recipiente.parametrizacion_recipiente && recipiente.parametrizacion_recipiente.id) {
            recipientesSet.add(recipiente.parametrizacion_recipiente.id);
          }
        });
      }
    });
    
    return Array.from(recipientesSet).map(id => {
      // Buscar primero en los datos de las visitas
      let recipienteData = null;
      visitasData.forEach(visita => {
        if (visita.recipientes && Array.isArray(visita.recipientes)) {
          visita.recipientes.forEach(recipiente => {
            if (recipiente.parametrizacion_recipiente && recipiente.parametrizacion_recipiente.id == id) {
              recipienteData = recipiente.parametrizacion_recipiente;
            }
          });
        }
      });
      
      // Si no se encuentra, buscar en los datos de la API
      if (!recipienteData && Array.isArray(recipientesData)) {
        recipienteData = recipientesData.find(r => r.id == id);
      }
      
      return {
        descripcion: recipienteData ? recipienteData.descripcion : (recipientesMap[id] || id),
        id: id
      };
    });
  };
  
  const recipientesUnicos = getRecipientesUnicos();
  
  // Obtener información del caso para el header
  const numeroPQR = casoData?.numero_reclamo || '';
  const cuentaSuscriptor = casoData?.aforos?.[0]?.cuenta_contrato || '';
  const fechaActual = new Date().toLocaleDateString('es-ES');
  
  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    // Agregar hora para evitar problemas de zona horaria
    const fechaObj = new Date(fecha + 'T12:00:00');
    return fechaObj.toLocaleDateString('es-ES');
  };
  
  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return '';
    return hora;
  };
  
  // Función para obtener unidades de recipiente por día
  const getUnidadesRecipiente = (visita) => {
    if (!visita || !visita.recipientes) return '';
    
    const totalUnidades = visita.recipientes.reduce((total, recipiente) => {
      return total + (recipiente.unidad || recipiente.unidades || 0);
    }, 0);
    
    return totalUnidades > 0 ? `${totalUnidades}` : '';
  };

  // Función para obtener detalles de recipientes por día
  const getDetalleRecipientes = (visita) => {
    if (!visita || !visita.recipientes) return '';
    
    return visita.recipientes.map(recipiente => 
      `${recipiente.tipo_recipiente}: ${recipiente.unidad || recipiente.unidades || 0}`
    ).join('\n');
  };

  return (
    <Page size="A4" style={styles.page}>
      {/* Header con logo, título e información */}
      <View style={styles.header}>
        <View style={styles.logoSection}>
          <Image style={styles.logo} src={logoLimpia} />
        </View>
        <View style={styles.titleSection}>
          <Text style={styles.titleSmall}>AFORO DE LOS RESIDUOS</Text>
        </View>
        <View style={styles.headerInfoSection}>
          <View style={styles.headerInfoBox}>
            <View style={styles.headerInfoItemHorizontal}>
              <Text style={styles.headerInfoLabel}>No. DE PQRS:</Text>
              <Text style={styles.headerInfoValue}>{numeroPQR}</Text>
            </View>
            <View style={styles.headerInfoItemHorizontal}>
              <Text style={styles.headerInfoLabel}>CUENTA SUSCRIPTOR:</Text>
              <Text style={styles.headerInfoValue}>{cuentaSuscriptor}</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Layout principal */}
      <View style={styles.mainLayout}>
        {/* Área de semanas - ahora ocupa todo el ancho */}
        <View style={styles.weeksAreaFull}>
          {semanasData.map((semanaData, semanaIndex) => (
            <View key={semanaData.numeroSemana} style={styles.weekContainer}>
              {/* Header de semana */}
              <View style={styles.weekHeader}>
                <Text style={styles.weekHeaderText}>SEMANA No. {semanaData.numeroSemana}</Text>
              </View>

              {/* Contenido de la semana */}
              <View style={styles.weekContent}>
                <View style={styles.weekTable}>
                  {/* Header de días - solo mostrar días con visitas */}
                  <View style={styles.weekTableHeader}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Recipientes</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => (
                    <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayHeaderCellLastDynamic : estilosDinamicos.dayHeaderCellDynamic}>
                      <Text style={styles.dayHeaderText}>{dia}</Text>
                    </View>
                  ))}
                </View>

                {/* Filas de recipientes */}
                {recipientesUnicos.map((recipiente, recipienteIndex) => (
                  <View key={recipiente.id} style={recipienteIndex === recipientesUnicos.length - 1 && semanaIndex === semanasData.length - 1 ? styles.weekDataRowLast : styles.weekDataRow}>
                    <View style={estilosDinamicos.rowLabelCellDynamic}>
                      <Text style={styles.recipientCodeInTable}>{recipiente.id}</Text>
                      <Text style={styles.recipientNameInTable}>{recipiente.descripcion}</Text>
                    </View>
                    {semanaData.diasConVisitas.map((dia, diaIndex) => {
                      const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                      const unidadesRecipiente = visita ? 
                        visita.recipientes?.find(r => r.parametrizacion_recipiente?.id == recipiente.id)?.unidad || 
                        visita.recipientes?.find(r => r.parametrizacion_recipiente?.id == recipiente.id)?.unidades || 0 : 0;
                      
                      return (
                        <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                          <View style={styles.dayContent}>
                            <Text style={styles.recipientUnits}>
                              {unidadesRecipiente > 0 ? unidadesRecipiente : ''}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                ))}

                {/* Fila de Fecha */}
                <View style={styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Fecha</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        <View style={styles.dayContent}>
                          <Text style={styles.visitDate}>
                            {visita ? formatearFecha(visita.fecha_visita) : ''}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* Fila de Hora */}
                <View style={styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Hora</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        <View style={styles.dayContent}>
                          <Text style={styles.visitTime}>
                            {visita ? formatearHora(visita.hora_visita) : ''}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* Fila de Nombre del Usuario */}
                <View style={styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Nombre del Usuario</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        <View style={styles.dayContent}>
                          <Text style={styles.visitName}>
                            {visita ? (visita.nombre_representante || '') : ''}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* Fila de Firma de Usuario */}
                <View style={styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Firma de Usuario</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        {visita ? <SignatureImage signaturePath={visita.firma_representante} /> : <View />}
                      </View>
                    );
                  })}
                </View>

                {/* Fila de Nombre del Visitador */}
                <View style={styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Nombre del Visitador</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        <View style={styles.dayContent}>
                          <Text style={styles.visitName}>
                            {visita ? (visita.nombre_visitador || casoData?.aforos?.[0]?.visitador_nombre || '') : ''}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* Fila de Firma del Visitador */}
                <View style={semanaIndex === semanasData.length - 1 ? styles.weekDataRowLast : styles.weekDataRow}>
                  <View style={estilosDinamicos.rowLabelCellDynamic}>
                    <Text style={styles.rowLabelText}>Firma del Visitador</Text>
                  </View>
                  {semanaData.diasConVisitas.map((dia, diaIndex) => {
                    const visita = getVisitaPorDiaEnSemana(dia, semanaData);
                    return (
                      <View key={dia} style={diaIndex === semanaData.diasConVisitas.length - 1 ? estilosDinamicos.dayDataCellLastDynamic : estilosDinamicos.dayDataCellDynamic}>
                        {visita ? <SignatureImage signaturePath={visita.firma_visitador} /> : <View />}
                      </View>
                    );
                  })}
                  </View>
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Footer legal */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Autorizo de manera previa, expresa e informada a Ciudad Limpia para que directa o indirectamente, realice cualquier operación de tratamiento de mis Datos Personales, a nivel nacional e internacional, incluso con terceros ubicados en países con niveles adecuados de protección conforme a lo dispuesto en la Ley 1581 de 2012 y Decreto 1377 de 2013. Para conocer más sobre los derechos que les asisten a los titulares de los Datos Personales, las formas para ejercerlos y las finalidades y tipos de tratamiento a los que los Datos Personales serán sometidos, los invitamos a consultar la Política de Tratamiento de la Información de la Compañía, disponible en:
        </Text>
        <View style={styles.footerLinks}>
          <Text style={styles.footerLink}>Cali: https://www.ciudadlimpiacali.com.co/wp-content/uploads/documentos/politica_tratamiento_cali.pdf, Neiva: https://www.ciudadlimpianeiva.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf</Text>
          <Text style={styles.footerLink}>Huila: https://www.ciudadlimpiadelhulla.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf, Bogotá: https://www.ciudadlimpia.com.co/Politicas-Tratamiento-de-la-informacion.pdf</Text>
        </View>
        <Text style={styles.footerCode}>
          BFCM-03{"\n"}03/2022 Ed.7
        </Text>
      </View>
    </Page>
  );
};

export default AforoPDFDocument;